// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Center, Divider, Flex, Loader, Select, Space, Stack, Switch, Text, TextInput, Textarea } from '@mantine/core';
import { IconWorldWww, IconPalette, IconWebhook, IconExternalLink, IconAppWindow } from '@tabler/icons';
import validator from 'validator';
import { useContext } from 'react';
import { DataS } from '../../../../Service/DataS';
import { ToastS } from '../../../../Service/ToastS';
import SemanticFormStyle from '../../../../Types/Enums/SemanticFormStyle';
import ContactFormStyleOptions from '../ContactFormStyleOptions';
import { BookitupFormContext, useFormMetadata } from '../../../../Service/Context/BookitupFormContext';
import ChangesDetectedAlert from '../ChangesDetectedAlert';
import { LicenceS } from '../../../../Service/LicenceS';
import { ViolationContext } from '../../../../Service/Context/ViolationContext';
import { ThemeText } from '../../../../Atoms/ThemeText';
import LinkText from '../../../../Atoms/LinkText';
import { RoutingS } from '../../../../Service/RoutingS';

const FormSettings = () => {
  const { bookitupForm, loading, updateForm, toggleHChaptcha, addingHChaptcha } = useContext(BookitupFormContext);
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const { hcapthaPresent } = useFormMetadata();

  if (loading || !bookitupForm) {
    return (
      <Center>
        <Loader size="xl" pos="absolute" top="40%" />
      </Center>
    );
  }
  const {
    name,
    domain,
    acknowledgementText,
    actionButtonText,
    recaptchaSecret,
    formStyle,
    outOfSync,
    webhookUrl,
    redirectUrl,
  } = bookitupForm;

  return (
    <>
      {outOfSync && <ChangesDetectedAlert />}
      <ThemeText weight="bolder" size="lg" mb="md" mt="md">
        Allgemein
      </ThemeText>
      <Stack sx={{ maxWidth: 600 }}>
        <TextInput
          required
          label="Bezeichnung"
          defaultValue={name}
          onBlur={(e) => updateForm({ name: e.currentTarget.value })}
          error={validator.isEmpty(name) && 'Bitte geben Sie einen gültigen Formularnamen an.'}
        />
        <TextInput
          required
          label="Domain"
          defaultValue={domain}
          icon={<IconWorldWww size={20} />}
          placeholder="www.example.com"
          onBlur={(e) => {
            const val = e.currentTarget.value;
            if (validator.isFQDN(val) || val === '') {
              updateForm({ domain: val });
            } else {
              ToastS.error('invalid-domain', 'Bitte trage eine gültige Domain ein.');
            }
          }}
        />
        <TextInput
          label="reCAPTCHA Secret Key (Achtung: nicht DSGVO-konform!)"
          defaultValue={recaptchaSecret}
          onBlur={(e) => updateForm({ recaptchaSecret: e.currentTarget.value })}
        />
        <Flex align="center">
          <div style={{ flexGrow: 1 }}>
            <ThemeText weight="bolder">HChaptcha</ThemeText>
            <Text size="xs" color="dimmed" mt={5}>
              Schütze dein Formular vor Spam und automatisierten Bots, indem du hCaptcha aktivierst. Nutzer müssen damit
              bestätigen, dass sie echte Menschen sind.
            </Text>
          </div>
          <Space w="xl" />
          {addingHChaptcha && <Loader size="md" />}
          {!addingHChaptcha && <Switch checked={hcapthaPresent} onChange={toggleHChaptcha} />}
        </Flex>
        <LinkText onClick={() => RoutingS.openInNewTab('https://www.hcaptcha.com/')}>
          <IconExternalLink size={16} style={{ marginBottom: 3, marginRight: 3 }} /> https://www.hcaptcha.com/{' '}
        </LinkText>
      </Stack>
      <Divider my={30} />
      <ThemeText weight="bolder" size="lg" mb="md" mt="xl">
        Formular absenden
      </ThemeText>
      <Stack sx={{ maxWidth: 600 }}>
        <TextInput
          label="Absende-Button-Beschriftung"
          placeholder="Anfrage senden"
          defaultValue={actionButtonText}
          onBlur={(e) => updateForm({ actionButtonText: e.currentTarget.value })}
        />
        <div>
          <Textarea
            label="Dankestext"
            defaultValue={acknowledgementText}
            placeholder="Vielen Dank für Ihre Anfrage!"
            onBlur={(e) => updateForm({ acknowledgementText: e.currentTarget.value })}
            minRows={3}
            maxLength={500}
          />
          <Text mt={5} size="xs" color="dimmed">
            In dieses Textfeld kannst Du entweder einen Text einfügen, der nach dem Absenden angezeigt werden soll.
          </Text>
        </div>

        <div>
          <TextInput
            label="Optional: Link zu deiner Danke-Seite"
            defaultValue={redirectUrl ?? ''}
            icon={<IconAppWindow size={20} />}
            onBlur={(e) => {
              const val = e.currentTarget.value;
              if (validator.isURL(val) || val === '') {
                updateForm({ redirectUrl: val });
              } else {
                ToastS.error('invalid-domain', 'Bitte trage eine gültige URL ein.');
              }
            }}
          />
          <Text mt={5} size="xs" color="dimmed">
            Falls du eine Danke-Seite hast, kannst du hier den Link eintragen.
          </Text>
        </div>
        <div>
          <TextInput
            label="Optional: Webhook-URL"
            defaultValue={webhookUrl ?? ''}
            icon={<IconWebhook size={20} />}
            onBlur={(e) => {
              const val = e.currentTarget.value;
              if (validator.isURL(val)) {
                executeWithLicenceCheck(LicenceS.Restrictions.CONTACT_FORM_ZAPIER, () =>
                  updateForm({ webhookUrl: val }),
                );
              }
              if (val === '') {
                updateForm({ webhookUrl: val });
              } else {
                ToastS.error('invalid-domain', 'Bitte trage eine gültige URL ein.');
              }
            }}
          />
          <Text mt={5} size="xs" color="dimmed">
            z.B. zu Zapier, Make, n8n etc., um die Daten der Anfrage in andere Systeme zu übertragen.
          </Text>
        </div>
      </Stack>
      <Divider my={30} />
      <ThemeText weight="bolder" size="lg" mb="md" mt="xl">
        Stile
      </ThemeText>
      <Select
        mb="md"
        label="Vorlagen für oft verwendete Farben"
        value={formStyle}
        icon={<IconPalette size={20} />}
        placeholder="Formularthema"
        data={DataS.bookitupFormStyleOptions}
        onChange={(val: SemanticFormStyle) => updateForm({ formStyle: val })}
        sx={{ maxWidth: 600 }}
      />
      <ContactFormStyleOptions />
      <Space h={50} />
    </>
  );
};

export default FormSettings;
